import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import { Router } from "@angular/router";
import { Subscription, Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";

import { Config } from "src/app/config";
import { environment } from "src/environments/environment";
import { ExamService } from "src/app/services/exam.service";
import { EventsHubService, EXAMMESSAGETYPE, ExamMessage } from "src/app/services/eventshub.service";
import { UserAuthService } from "src/app/services/userauth.service";
import { AdminAuthService } from "src/app/services/adminauth.service";
import { ModalDialogService } from "src/app/modals/modaldialog/modaldialog.service";
import { ServerResourcesPathPipe } from "src/app/pipe/serverresourcespath.pipe";
import { Exam } from "src/app/models/exam";
import { PlayerStatus } from "src/app/models/playerstatus";
import { PageObjectContainer } from "src/app/models/pageobjectcontainer";
import { PAGEOBJECTTYPE } from "src/app/models/pageobject";
import { PageObjectButton } from "src/app/models/pageobjectbutton";
import { SERVERRESPONSERESULT } from "src/app/models/serverresponse";
import { PageObjectEvaluation } from "src/app/models/pageobjectevaluation";
import { PageObjectSelection } from "src/app/models/pageobjectselection";
import { PageObjectInput } from "src/app/models/pageobjectinput";

@Component({
  selector: "hkt-exam",
  templateUrl: "./exam.component.html",
  styleUrls: ["./exam.component.scss"],
  host: {
    "(contextmenu)": "onCancelEvent($event)", // Cancel Context Menu (right click not working)
    "(dragstart)": "onCancelEvent($event)", // Cancel Context Menu (right click not working)
    "(window:resize)": "onWindowResize($event)",
  },
})
export class ExamComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("divWorkplace") divWorkplaceElementRef: ElementRef;
  @Input("mode") mode: string;
  @Input("userexamid") userExamId: number;
  @Output("logout") logoutEmitter: EventEmitter<void>;

  exam: Exam;
  pageX: number;
  pageY: number;
  pageScale: number;
  pageTransform: string;
  pageClass: string;
  examSubscription: Subscription;
  examMessageSubscription: Subscription;
  soundPlayer: Map<string, PlayerStatus>;
  saveExamSubject: Subject<void>;
  saveExamSubscription: Subscription;
  currentPageContainer: PageObjectContainer;
  onChangePage: Subscription;

  constructor(
    private examService: ExamService,
    private eventsHubService: EventsHubService,
    private modalDialogService: ModalDialogService,
    private serverResourcesPathPipe: ServerResourcesPathPipe,
    private userAuthService: UserAuthService,
    private adminAuthService: AdminAuthService,
    private router: Router,
  ) {
    this.soundPlayer = new Map();
    this.pageX = 0;
    this.pageY = 0;
    this.pageScale = 1;
    this.updatePageTransform();
    this.pageClass = "page";
    this.logoutEmitter = new EventEmitter<void>();

    this.currentPageContainer = new PageObjectContainer();
    this.currentPageContainer.scrollHorizontal = false;
    this.currentPageContainer.scrollVertical = false;
  }

  ngOnInit(): void {
    this.exam = this.examService.currentExam;
    this.examSubscription = this.examService.onCurrentExamChange.subscribe(exam => {
      this.exam = exam;
      if (this.mode == "view" || this.mode == "evaluate") {
        this.exam.currentPageIndex = 0;
      }
      if (this.mode == "view") {
        this.exam.pages.forEach(page => {
          page.objects.forEach(obj => {
            if (obj.type == PAGEOBJECTTYPE.Button) {
              let button = <PageObjectButton>obj;
              let action = button.action.toLocaleLowerCase();
              if (action.indexOf("evaluatepage") >= 0) {
                button.visible = false;
              }
            }
          });
        });
      }

      this.currentPageContainer.objects = this.exam.pages[this.exam.currentPageIndex].objects;

      this.updatePageClass();
      this.onPageChanged();
      this.onWindowResize(null);
    });
    this.examMessageSubscription = this.eventsHubService.onExamMessage.subscribe(msg => {
      if (msg.type == EXAMMESSAGETYPE.RunAction) {
        this.executeActions(msg.data);
      }
    });

    this.saveExamSubject = new Subject<void>();
    this.saveExamSubscription = this.saveExamSubject.pipe(debounceTime(2000)).subscribe(() => {
      if (this.mode == "exam") {
        this.saveUserExam();
      } else if (this.mode == "evaluate") {
        this.saveExamResults();
      }
    });

    this.onChangePage = this.eventsHubService.onChangePage.subscribe(pageIndex => {
      if (this.exam.currentPageIndex == pageIndex) {
        return;
      }
      this.exam.currentPageIndex = pageIndex;
      this.onPageChanged();
    });
  }

  ngOnDestroy(): void {
    this.examSubscription.unsubscribe();
    this.examMessageSubscription.unsubscribe();
    this.saveExamSubscription.unsubscribe();
    this.onChangePage.unsubscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.onWindowResize(null);
    }, 0);
  }

  onCancelEvent(event): boolean {
    return false;
  }

  onWindowResize(event) {
    let divWorkplace = this.divWorkplaceElementRef.nativeElement;
    const margin = 0;
    const header = 50;

    let scaleX = (divWorkplace.offsetWidth - 2 * margin) / this.exam.pageWidth;
    let scaleY = (divWorkplace.offsetHeight - header - 2 * margin) / this.exam.pageHeight;
    this.pageScale = scaleX < scaleY ? scaleX : scaleY;
    this.pageX = (divWorkplace.offsetWidth - this.exam.pageWidth) / 2;
    this.pageY = (divWorkplace.offsetHeight - header - this.exam.pageHeight) / 2 + header;
    this.updatePageTransform();
  }

  private updatePageClass() {
    let pageClass = "page";
    if (this.exam.pages[this.exam.currentPageIndex].background != "") {
      pageClass += " " + this.exam.pages[this.exam.currentPageIndex].background;
    }
    this.pageClass = pageClass;
  }

  private updatePageTransform() {
    this.pageTransform =
      "matrix(" + this.pageScale + ", 0, 0, " + this.pageScale + ", " + this.pageX + ", " + this.pageY + ")";
  }

  private saveUserExam(): void {
    this.examService.saveUserExam(this.userAuthService.currentUser.id).subscribe(response => {
      if (response.result == SERVERRESPONSERESULT.Error) {
        this.modalDialogService.showAlert(Config.APP_NAME, "Error connecting to the server");
      } else if (response.result == SERVERRESPONSERESULT.NotLoggedIn) {
        this.modalDialogService.showAlert(
          Config.APP_NAME,
          "There is a problem with your login information, please login again",
          () => {
            this.logoutEmitter.emit();
          },
        );
      }
    });
  }

  private saveExamResults(): void {
    this.examService.saveExamResults(this.userExamId).subscribe(response => {
      if (response.result == SERVERRESPONSERESULT.NotLoggedIn) {
        // The grader saves on its own when the page is turned, so it saves again after the login
        this.adminAuthService.askLogin(Config.ADMIN_SESSION_ENDED_AUTOSAVE).subscribe(loggedIn => {
          if (loggedIn) {
            this.saveExamResults();
          }
        });
      } else if (response.result != SERVERRESPONSERESULT.OK) {
        this.modalDialogService.showAlert(Config.APP_NAME, response.error || "Error connecting to the server");
      }
    });
  }

  private submitUserExam(): void {
    const loading = this.modalDialogService.createModal();
    loading.setTitle(Config.APP_NAME);
    loading.setBody("Submitting ...");
    loading.setShowCloseButton(false);
    loading.show();

    this.examService.submitUserExam(this.userAuthService.currentUser.id).subscribe(response => {
      this.modalDialogService.dismissModal(loading);

      if (response.result == SERVERRESPONSERESULT.OK) {
        this.modalDialogService.showAlert(Config.APP_NAME, "Exam submitted. Thank you", () => {
          this.userAuthService.logout();
          this.router.navigate(["login", this.exam.id]);
        });
      } else if (response.result == SERVERRESPONSERESULT.NotLoggedIn) {
        this.modalDialogService.showAlert(
          Config.APP_NAME,
          "There is a problem with your login information, please login again",
          () => {
            this.logoutEmitter.emit();
          },
        );
      } else {
        this.modalDialogService.showAlert(Config.APP_NAME, "Error connecting to the server");
      }
    });
  }

  private submitExamResults(): void {
    const loading = this.modalDialogService.createModal();
    loading.setTitle(Config.APP_NAME);
    loading.setBody("Submitting ...");
    loading.setShowCloseButton(false);
    loading.show();

    this.examService.submitExamResults(this.userExamId).subscribe(response => {
      this.modalDialogService.dismissModal(loading);

      if (response.result == SERVERRESPONSERESULT.NotLoggedIn) {
        this.adminAuthService.askLogin(Config.ADMIN_SESSION_ENDED).subscribe();
      } else if (response.result != SERVERRESPONSERESULT.OK) {
        this.modalDialogService.showAlert(Config.APP_NAME, response.error || "Error connecting to the server");
      } else {
        this.modalDialogService.showAlert(Config.APP_NAME, "Exam evaluated. Thank you", () => {
          window.location.href = environment.backofficeUrl + "/admin/online-study";
        });
      }
    });
  }

  private onPageChanged(): void {
    this.currentPageContainer.objects = this.exam.pages[this.exam.currentPageIndex].objects;

    let msg = new ExamMessage();
    msg.type = EXAMMESSAGETYPE.RunAction;
    msg.data = "";

    if (this.exam.currentPageIndex <= 0) {
      msg.data += "Hide(PREV_PAGE);";
    } else {
      msg.data += "Show(PREV_PAGE);";
    }

    if (this.exam.currentPageIndex >= this.exam.pages.length - 1) {
      msg.data += "Hide(NEXT_PAGE);";
    } else {
      msg.data += "Show(NEXT_PAGE);";
    }

    if (this.exam.currentPageIndex == this.exam.pages.length - 1) {
      if (this.mode == "exam" || this.mode == "evaluate") {
        msg.data += "Show(SUBMIT);";
      } else {
        msg.data += "Hide(SUBMIT);";
      }
    } else {
      msg.data += "Hide(SUBMIT);";
    }

    if (this.mode == "evaluate") {
      let hasEvaluation = false;
      for (let obj of this.currentPageContainer.objects) {
        if (obj.type == PAGEOBJECTTYPE.Evaluation) {
          hasEvaluation = true;
          break;
        }
      }

      if (hasEvaluation) {
        msg.data += "Show(EVALUATION);";
      } else {
        msg.data += "Hide(EVALUATION);";
      }
    }

    this.examService.setCurrentPage(this.exam.currentPageIndex);
    this.eventsHubService.setExamMessage(msg);
    this.updatePageClass();
  }

  private executeActions(actions: string): void {
    let actionsList = actions.split(";");
    for (let i = 0; i < actionsList.length; i++) {
      let action = actionsList[i].trim();
      if (action != "") {
        this.executeAction(action);
      }
    }
  }

  private executeAction(action: string): void {
    let functionName: string;
    let params: string[];
    let pos1, pos2;

    // Parse action
    pos1 = action.indexOf("(");
    if (pos1 == -1) {
      functionName = action;
    } else {
      functionName = action.substring(0, pos1);
      pos2 = action.indexOf(")");
      if (pos2 != -1) {
        let paramsStr = action.substring(pos1 + 1, pos2);
        params = paramsStr.split(",");
        for (let i = 0; i < params.length; i++) {
          params[i] = params[i].trim();
        }
      }
    }

    // Execute action
    functionName = functionName.toUpperCase();
    if (functionName == "NEXTPAGE") {
      // NextPage()
      if (this.exam.currentPageIndex >= this.exam.pages.length - 1) {
        return;
      }
      this.exam.currentPageIndex++;
      this.onPageChanged();

      this.saveExamSubject.next();
    } else if (functionName == "PREVPAGE") {
      // PrevPage()
      if (this.exam.currentPageIndex <= 0) {
        return;
      }
      this.exam.currentPageIndex--;
      this.onPageChanged();

      this.saveExamSubject.next();
    } else if (functionName == "EVALUATEPAGE") {
      // EvaluatePage()
      let objects = this.exam.pages[this.exam.currentPageIndex].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].type == PAGEOBJECTTYPE.Evaluation) {
          let evaluation = <PageObjectEvaluation>objects[i];
          evaluation.evaluated = true;
          evaluation.evaluationResult = this.parseEvaluation(evaluation.evaluation, evaluation.score);
          if (evaluation.evaluationResult == evaluation.score) {
            this.executeActions(evaluation.actionGood);
          } else if (evaluation.evaluationResult == 0) {
            this.executeActions(evaluation.actionBad);
          }
        }
      }

      this.saveExamSubject.next();
    } else if (functionName == "SUBMITEXAM") {
      // SubmitExam()
      if (this.mode == "evaluate") {
        let incomplete = false;
        for (let i = 0; i < this.exam.pages.length; i++) {
          let page = this.exam.pages[i];
          for (let j = 0; j < page.objects.length; j++) {
            let obj = page.objects[j];
            if (obj.type == PAGEOBJECTTYPE.Evaluation) {
              let evaluation = <PageObjectEvaluation>obj;
              if (!evaluation.evaluated) {
                incomplete = true;
                break;
              }
              if (evaluation.evaluationResult < 0) {
                incomplete = true;
                break;
              }
            }
          }
          if (incomplete) {
            this.modalDialogService.showAlert(
              Config.APP_NAME,
              "There is evaluation missing on page '" + page.name + "'",
            );
            break;
          }
        }
        if (incomplete) {
          return;
        }

        this.modalDialogService.showConfirmation(
          Config.APP_NAME,
          "Did you finish to evaluate the exam? You won't be able to do any modification",
          () => {
            this.saveExamSubscription.unsubscribe();
            this.submitExamResults();
          },
        );
      } else if (this.mode == "exam") {
        this.modalDialogService.showConfirmation(
          Config.APP_NAME,
          "Are you sure to submit the exam? You won't be able to do any modification",
          () => {
            this.saveExamSubscription.unsubscribe();
            this.submitUserExam();
          },
        );
      }
    } else if (functionName == "DESELECT") {
      // Deselect(GroupName)
      if (params.length < 1) {
        return;
      }
      let objects = this.exam.pages[this.exam.currentPageIndex].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].type == PAGEOBJECTTYPE.Selection) {
          let selection = <PageObjectSelection>objects[i];
          if (selection.groupName == params[0]) {
            selection.selected = false;
          }
        }
      }
    } else if (functionName == "SELECT") {
      // Select(GroupName, ObjectName)
      if (params.length < 2) {
        return;
      }
      let objects = this.exam.pages[this.exam.currentPageIndex].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].type == PAGEOBJECTTYPE.Selection) {
          let selection = <PageObjectSelection>objects[i];
          if (selection.groupName == params[0] && selection.name == params[1]) {
            selection.selected = true;
          }
        }
      }
      this.saveExamSubject.next();
    } else if (functionName == "SAVEEXAM") {
      this.saveExamSubject.next();
    } else if (functionName == "SHOW") {
      // Show(ObjectName)
      if (params.length < 1) {
        return;
      }
      let objects = this.exam.pages[0].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].name == params[0]) {
          objects[i].visible = true;
        }
      }
      objects = this.exam.pages[this.exam.currentPageIndex].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].name == params[0]) {
          objects[i].visible = true;
        }
      }
    } else if (functionName == "HIDE") {
      // Hide(ObjectName)
      if (params.length < 1) {
        return;
      }
      let objects = this.exam.pages[0].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].name == params[0]) {
          objects[i].visible = false;
        }
      }
      objects = this.exam.pages[this.exam.currentPageIndex].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].name == params[0]) {
          objects[i].visible = false;
        }
      }
    } else if (functionName == "PLAYSOUND") {
      // Play(SoundName)
      if (params.length < 1) {
        return;
      }
      let sound = this.exam.library.getSound(params[0]);
      if (sound == null) {
        return;
      }

      let player: PlayerStatus = this.soundPlayer.get(sound.name);
      if (!player) {
        player = {
          sound: sound,
          isPlaying: false,
          audio: new Audio(),
        };
        player.audio.onloadeddata = function() {
          player.audio.play();
        };
        player.audio.onplaying = function() {
          player.isPlaying = true;
        };
        player.audio.onpause = function() {
          player.isPlaying = false;
        };
        player.audio.src = this.serverResourcesPathPipe.transform(
          sound.source,
          Config.SERVER_UPLOADFOLDER,
          "no_sanitizer",
        );
        player.audio.load();

        this.soundPlayer.set(sound.name, player);
      } else {
        if (player.isPlaying) {
          player.audio.pause();
        } else {
          player.audio.play();
          player.audio.currentTime = 0;
        }
      }
    }
  }

  private parseEvaluation(evaluations: string, maxScore: number): number {
    if (!evaluations) {
      evaluations = "";
    }

    if (evaluations.indexOf("(") == -1) {
      // Direct expresion. Separate in evaluations
      if (evaluations.indexOf("&") != -1) {
        // All Expresion should be AND
        let expresions = evaluations.split("&");
        for (let i = 0; i < expresions.length; i++) {
          let result = this.executeEvaluation(expresions[i], maxScore);
          if (result == -1) {
            return result;
          } else if (result == 0) {
            return 0;
          }
        }
        return maxScore;
      } else if (evaluations.indexOf("|") != -1) {
        // All Expresion should be OR
        let expresions = evaluations.split("|");
        for (let i = 0; i < expresions.length; i++) {
          let result = this.executeEvaluation(expresions[i], maxScore);
          if (result == -1) {
            return result;
          } else if (result == maxScore) {
            return maxScore;
          }
        }
        return 0;
      } else {
        // Single Expresion
        return this.executeEvaluation(evaluations, maxScore);
      }
    } else {
      // Separate expresions
      let expresions = [];
      let pos1 = 0;
      let pos2 = pos1;
      let counter = 0;
      let mode = 0;
      do {
        if (evaluations[pos2] == "(") {
          if (counter == 0) {
            pos1 = pos2 + 1;
          }
          counter++;
        } else if (evaluations[pos2] == ")") {
          counter--;
          if (counter == 0) {
            let exp = evaluations.substring(pos1, pos2).trim();
            expresions.push(exp);
            pos1 = pos2 + 1;
          }
        } else if (evaluations[pos2] == "&" && counter == 0) {
          mode = 1; // AND
        } else if (evaluations[pos2] == "|" && counter == 0) {
          mode = 2; // OR
        }
        pos2++;
      } while (pos2 < evaluations.length);

      // Evaluate Expresions
      if (mode == 1) {
        // AND
        for (let i = 0; i < expresions.length; i++) {
          let result = this.parseEvaluation(expresions[i], maxScore);
          if (result == -1) {
            return result;
          } else if (result == 0) {
            return 0;
          }
        }
        return maxScore;
      } else if (mode == 2) {
        // OR
        for (let i = 0; i < expresions.length; i++) {
          let result = this.parseEvaluation(expresions[i], maxScore);
          if (result == -1) {
            return result;
          } else if (result == maxScore) {
            return maxScore;
          }
        }
        return 0;
      }
    }
    return 0;
  }

  private executeEvaluation(evaluation: string, maxScore: number): number {
    let params = evaluation.split(":");
    for (let i = 0; i < params.length; i++) {
      params[i] = params[i].trim();
    }
    params[0] = params[0].toUpperCase();

    if (params[0] == "") {
      return -1;
    } else if (params[0] == "ISSELECTED") {
      // isSelected : ObjectName
      if (params.length < 2) {
        return 0;
      }
      let objects = this.exam.pages[this.exam.currentPageIndex].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].type == PAGEOBJECTTYPE.Selection && objects[i].name == params[1]) {
          let selection = <PageObjectSelection>objects[i];
          if (selection.selected) {
            return maxScore;
          }
          return 0;
        }
      }
    } else if (params[0] == "ISNOTSELECTED") {
      // isNotSelected : ObjectName
      if (params.length < 2) {
        return 0;
      }
      let objects = this.exam.pages[this.exam.currentPageIndex].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].type == PAGEOBJECTTYPE.Selection && objects[i].name == params[1]) {
          let selection = <PageObjectSelection>objects[i];
          if (!selection.selected) {
            return maxScore;
          }
          return 0;
        }
      }
    } else if (params[0] == "ISINPUTVALUE") {
      // isInputValue : ObjectName : value
      if (params.length < 3) {
        return 0;
      }
      let objects = this.exam.pages[this.exam.currentPageIndex].objects;
      for (let i = 0; i < objects.length; i++) {
        if (objects[i].type == PAGEOBJECTTYPE.Input && objects[i].name == params[1]) {
          let input = <PageObjectInput>objects[i];
          if (input.text.toLowerCase() == params[2].toLowerCase()) {
            return maxScore;
          }
          return 0;
        }
      }
    }

    return 0;
  }
}
